import React, { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const tooltipStyles = {
  position: 'absolute',
  background: '#000',
  color: '#fff',
  padding: '5px 10px',
  borderRadius: '5px',
  fontSize: '15px',
  zIndex: 9999,
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
} as const;

interface TooltipPosition {
  top: number;
  left: number;
}

interface ComponentProps {
  targetId: string;
  content: ReactNode;
  placement?: 'top' | 'right' | 'bottom' | 'left';
  styleOverrides?: CSSProperties;
  locationId?: string;
  limitWidth?: number;
}

const TooltipPortal: React.FC<ComponentProps> = ({
  targetId,
  content,
  placement = 'top',
  locationId,
  styleOverrides = {},
  limitWidth,
}) => {
  const [position, setPosition] = useState<TooltipPosition | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const fadeInDuration = 300;
  const fadeOutDuration = 100;
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const target = document.getElementById(targetId);
    const anchor = locationId ? document.getElementById(locationId) : target;
    if (!target || !anchor) return;

    const calculatePosition = (): TooltipPosition => {
      const rect = anchor.getBoundingClientRect();
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      const scrollX = window.scrollX || document.documentElement.scrollLeft;

      switch (placement) {
        case 'right':
          return {
            top: rect.top + scrollY + rect.height / 2,
            left: rect.left + scrollX + rect.width + 8,
          };
        case 'bottom':
          return {
            top: rect.top + scrollY + rect.height + 8,
            left: rect.left + scrollX + rect.width / 2,
          };
        case 'left':
          return {
            top: rect.top + scrollY + rect.height / 2,
            left: rect.left + scrollX - 8,
          };
        case 'top':
        default:
          return {
            top: rect.top + scrollY - 8,
            left: rect.left + scrollX + rect.width / 2,
          };
      }
    };

    const showTooltip = () => {
      setPosition(calculatePosition());
      setIsMounted(true);
      setTimeout(() => setIsVisible(true), 10);
    };

    const hideTooltip = () => {
      setIsVisible(false);
      setTimeout(() => setIsMounted(false), fadeOutDuration);
    };

    target.addEventListener('mouseenter', showTooltip);
    target.addEventListener('mouseleave', hideTooltip);

    return () => {
      target.removeEventListener('mouseenter', showTooltip);
      target.removeEventListener('mouseleave', hideTooltip);
    };
  }, [targetId, placement, locationId]);

  if (!isMounted || !position) return null;

  const transformMap: Record<string, string> = {
    top: 'translate(-50%, -100%)',
    right: 'translate(0, -50%)',
    bottom: 'translate(-50%, 0)',
    left: 'translate(-100%, -50%)',
  };

  // Apply width limitation and adjust whiteSpace accordingly
  const widthStyles = limitWidth
    ? {
        maxWidth: `${limitWidth}px`,
        whiteSpace: 'normal' as const,
        wordWrap: 'break-word' as const,
      }
    : {
        whiteSpace: tooltipStyles.whiteSpace,
      };

  return createPortal(
    <div
      ref={tooltipRef}
      style={{
        ...tooltipStyles,
        ...widthStyles,
        ...{
          top: position.top,
          left: position.left,
          transform: transformMap[placement],
          opacity: isVisible ? 1 : 0,
          transition: `opacity ${isVisible ? fadeInDuration : fadeOutDuration}ms ease-in-out`,
        },
        ...styleOverrides,
      }}
    >
      {content}
    </div>,
    document.body,
  );
};

export default TooltipPortal;
